import Button from '@components/Button'
import ErrorPopup from '@components/ErrorPopup'
import Picker from '@components/Picker'
import RainbowTextField from '@components/RainbowTextField/RainbowTextField'
import { CanisterInfo } from '@plug-core/idls/topUp/topUp'
import { Box, FormControlLabel, InputAdornment, Stack, Switch, Typography } from '@mui/material'
import { CanisterUpdateDetails } from '@plug-core/hooks/useTopup'
import { logger } from '@plug-core/logger'
import { validateCanisterId } from '@plug-core/utils/validationHelper'
import React, { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Prompt from '@components/Prompt'
import { HiInformationCircle } from 'react-icons/hi'
import useConfig from '@plug-core/hooks/useConfig'

interface Props {
  hideSelf: () => void
  addCanister: (canister: CanisterUpdateDetails) => Promise<boolean>
  updateCanister?: (canister: CanisterUpdateDetails) => Promise<boolean>
  newCanister: boolean
  canister?: CanisterInfo
  prefilledCanisterId?: string
}

type TopupMode = 'SNS' | 'Blackhole' | 'Manual'

const TOPUP_TYPES: { key: TopupMode; humanValue: string }[] = [
  { key: 'SNS', humanValue: 'SNS' },
  { key: 'Blackhole', humanValue: 'Blackhole' },
  { key: 'Manual', humanValue: 'Manual' },
]

const CanisterForm: FC<Props> = ({
  hideSelf,
  addCanister,
  updateCanister,
  newCanister,
  canister,
  prefilledCanisterId,
}) => {
  const [submitLoading, setSubmitLoading] = useState(false)

  const [canisterName, setCanisterName] = useState<string>('')
  const [canisterId, setCanisterId] = useState<string>('')
  const [topupAmount, setTopupAmount] = useState<string>('')
  const [minCycles, setMinCyles] = useState<string>('')
  const [autoTopup, setAutoTopup] = useState<boolean>(false)
  const [snsRoot, setSnsRoot] = useState<string>('')
  const [saveError, setSaveError] = useState<string>()
  const [topupMode, setTopupMode] = useState<TopupMode>('Manual')
  const [prompt, setPrompt] = useState<{
    title: string
    description: string
  } | null>(null)

  const { t } = useTranslation()
  const { activeSns } = useConfig()

  useEffect(() => {
    if (!canister) return

    setCanisterName(canister.name)
    setCanisterId(canister.id.toString())
    setTopupAmount(convertToTC(canister.topup_amount))
    setMinCyles(convertToTC(canister.min_cycles))
    setAutoTopup(canister.auto_topup)
    setTopupMode(Object.keys(canister.topup_type)[0] as TopupMode)
    setSnsRoot(canister.sns_root[0]?.toString() || '')
  }, [canister])

  useEffect(() => {
    prefilledCanisterId && setCanisterId(prefilledCanisterId)
  }, [prefilledCanisterId])

  const convertToTC = (amount: BigInt) => {
    return Number(amount) === 0 ? '' : (Number(amount) * 10 ** -12).toString()
  }

  const handleSubmit = () => {
    if (!canisterId) return

    setSubmitLoading(true)

    const method = newCanister ? addCanister : updateCanister

    method?.({
      canisterId,
      name: canisterName,
      autoTopup,
      topupAmount,
      topupThreshold: minCycles,
      topupMode,
      snsRoot,
    })
      .then(() => {
        hideSelf()
      })
      .catch((e) => {
        logger.debug(e)
        setSaveError(t(`topup.saveError.${Object.keys(e)[0]}`))
      })
      .finally(() => setSubmitLoading(false))
  }

  const onInfoClick = (event: any, topupMode: TopupMode) => {
    event.stopPropagation()

    setPrompt({
      title: t(`topup.${topupMode}.title`),
      description: t(`topup.${topupMode}.description`),
    })
  }

  const snsPickerValues = useMemo(
    () =>
      activeSns.map((sns) => ({
        key: sns.rootCanisterId || '',
        humanValue: sns.symbol,
      })),
    [activeSns]
  )

  return (
    <Stack p={3} spacing={1.5}>
      <Typography variant="h6" textAlign="center">
        {newCanister ? 'Add Canister' : 'Update Canister Settings'}
      </Typography>

      <RainbowTextField
        placeholder={t('topup.canisterForm.canisterId')}
        value={canisterId}
        onChange={(e) => setCanisterId(e.target.value)}
        error={!!canisterId && !validateCanisterId(canisterId)}
      />

      <RainbowTextField
        placeholder={t('topup.canisterForm.name')}
        value={canisterName}
        onChange={(e) => setCanisterName(e.target.value)}
      />

      <Stack direction="row" alignItems="center">
        <Stack flex={1}>
          <Picker
            values={TOPUP_TYPES}
            currentValue={topupMode}
            onValueChange={(newValue) => setTopupMode(newValue as TopupMode)}
            heading={t('topup.canisterForm.topupMode')}
          />
        </Stack>

        <Box position="absolute" right={36}>
          <HiInformationCircle onClick={(e) => onInfoClick(e, topupMode)} size={20} />
        </Box>
      </Stack>

      {topupMode === 'SNS' && (
        <Picker
          values={snsPickerValues}
          currentValue={snsRoot}
          onValueChange={setSnsRoot}
          heading={t('topup.canisterForm.sns')}
        />
      )}

      {topupMode !== 'Manual' && (
        <>
          <FormControlLabel
            control={
              <Switch
                checked={autoTopup}
                onChange={(e) => setAutoTopup(e.target.checked)}
                color="success"
              />
            }
            label={<Typography>{t('topup.canisterForm.autoTopup')}</Typography>}
          />

          {autoTopup && (
            <>
              <RainbowTextField
                placeholder={t('topup.canisterForm.topupAmount')}
                value={topupAmount}
                onChange={(e) => setTopupAmount(e.target.value)}
                endAdornment={<InputAdornment position="end">TC</InputAdornment>}
              />
              <RainbowTextField
                placeholder={t('topup.canisterForm.topupThreshold')}
                value={minCycles}
                onChange={(e) => setMinCyles(e.target.value)}
                endAdornment={<InputAdornment position="end">TC</InputAdornment>}
              />
            </>
          )}
        </>
      )}

      <Button variant="rainbow" loading={submitLoading} fullWidth onClick={handleSubmit}>
        {newCanister ? t('common.save') : t('common.update')}
      </Button>

      <ErrorPopup
        title="Save Error"
        description={saveError}
        visible={!!saveError}
        onDismiss={() => setSaveError(undefined)}
      />

      <Prompt show={!!prompt} close={() => setPrompt(null)}>
        <Stack alignItems="center">
          <Stack alignItems="center" mb={0.5}>
            <Typography variant="h6" mt={0.2}>
              {prompt?.title}
            </Typography>
            <Typography textAlign="center" mt={1} mb={2} color="text.light">
              {prompt?.description}
            </Typography>
          </Stack>

          <Button onClick={() => setPrompt(null)} variant="outlined">
            {t('common.understood')}
          </Button>
        </Stack>
      </Prompt>
    </Stack>
  )
}

export default CanisterForm
